import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { databaseService } from '../../services/database.service';
import { useLanguage } from '../../hooks/useLanguage';
import { i18nService } from '../../services/i18n.service';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Button from '../../components/common/Button';

const GalleryPage = () => {
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [galleryItems, galleryCategories] = await Promise.all([
          databaseService.getGalleryItems(),
          databaseService.getGalleryCategories()
        ]);
        setItems(galleryItems);
        setFilteredItems(galleryItems);
        setCategories(galleryCategories);
      } catch (error) {
        console.error('Error fetching gallery:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleCategoryFilter = (category) => {
    setSelectedCategory(category);
    if (category === 'all') {
      setFilteredItems(items);
    } else {
      setFilteredItems(items.filter(item => item.category === category));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="py-12">
      <div className="container mx-auto px-4">
        <h1 className="text-4xl font-display font-bold text-center mb-8">{t('gallery.title')}</h1>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-8">
          <Button
            variant={selectedCategory === 'all' ? 'primary' : 'secondary'}
            onClick={() => handleCategoryFilter('all')}
          >
            {t('gallery.all')}
          </Button>
          {categories.map((category) => (
            <Button
              key={category.id}
              variant={selectedCategory === category.name ? 'primary' : 'secondary'}
              onClick={() => handleCategoryFilter(category.name)}
            >
              {i18nService.getTranslatedField(category, 'name', currentLanguage) || category.name}
            </Button>
          ))}
        </div>

        {/* Gallery Grid */}
        {filteredItems.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredItems.map((item) => (
              <div
                key={item.id}
                className="group relative overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-shadow cursor-pointer"
                onClick={() => setSelectedImage(item)}
              >
                {item.mediaType === 'video' ? (
                  <div className="relative w-full h-64 bg-gray-900">
                    {item.videoSource === 'upload' ? (
                      <video
                        src={item.videoUrl}
                        className="w-full h-full object-cover"
                        controls={false}
                        muted
                      />
                    ) : (
                      <iframe
                        src={item.videoUrl}
                        title={item.title}
                        className="w-full h-full pointer-events-none"
                        allowFullScreen
                      />
                    )}
                    <div className="absolute top-2 right-2 bg-black bg-opacity-70 text-white px-2 py-1 rounded text-xs">
                      🎥 Video
                    </div>
                  </div>
                ) : (
                  <img
                    src={item.imageUrl}
                    alt={item.title}
                    className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                  <div className="text-white">
                    <h3 className="font-semibold text-lg">
                      {i18nService.getTranslatedField(item, 'title', currentLanguage) || item.title}
                    </h3>
                    {(i18nService.getTranslatedField(item, 'description', currentLanguage) || item.description) && (
                      <p className="text-sm text-gray-200">
                        {i18nService.getTranslatedField(item, 'description', currentLanguage) || item.description}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-center text-gray-600 text-lg">{t('gallery.noItems')}</p>
        )}
      </div>

      {/* Lightbox */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-95 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div className="max-w-5xl w-full">
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-4 right-4 text-white text-4xl hover:text-gray-300 transition-colors"
            >
              ×
            </button>
            {selectedImage.mediaType === 'video' ? (
              <div className="w-full" onClick={(e) => e.stopPropagation()}>
                {selectedImage.videoSource === 'upload' ? (
                  <video
                    src={selectedImage.videoUrl}
                    className="w-full aspect-video rounded-lg"
                    controls
                    autoPlay
                  />
                ) : (
                  <iframe
                    src={selectedImage.videoUrl}
                    title={selectedImage.title}
                    className="w-full aspect-video rounded-lg"
                    allowFullScreen
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  />
                )}
              </div>
            ) : (
              <img
                src={selectedImage.imageUrl}
                alt={selectedImage.title}
                className="w-full h-auto rounded-lg"
              />
            )}
            <div className="text-white text-center mt-4">
              <h3 className="text-2xl font-bold">
                {i18nService.getTranslatedField(selectedImage, 'title', currentLanguage) || selectedImage.title}
              </h3>
              {(i18nService.getTranslatedField(selectedImage, 'description', currentLanguage) || selectedImage.description) && (
                <p className="text-gray-300 mt-2">
                  {i18nService.getTranslatedField(selectedImage, 'description', currentLanguage) || selectedImage.description}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GalleryPage;
